import { isPublicId } from '@hlt/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { notFound, redirect, type ParsedLocation } from '@tanstack/react-router';
import { apiQueryOptions, idOfPublicId, rememberPublicIds } from '@hlt/web-kit/api';

/**
 * An organization address the browser has not seen yet (`/o/<public id>/…` from a bookmark or another
 * device). The router maps known public identifiers to UUIDs (`shortIdRewrite`); an
 * unknown one is looked up once among the person's organizations, then through the operator console's
 * organization search (only operators may use it), and the page opens again with the UUID. Anything else is
 * "not found", exactly like an unknown UUID. The API keeps receiving UUIDs and authorizes as before.
 */
export async function resolveOrganizationRef(
    queryClient: QueryClient,
    ref: string,
    location: ParsedLocation,
): Promise<void> {
    if (!isPublicId(ref)) return;
    const lookups = [
        () => queryClient.fetchQuery(apiQueryOptions('listMyOrganizations', { input: { limit: 100 } })),
        () => queryClient.fetchQuery(apiQueryOptions('operatorOrganizations', { input: { search: ref, limit: 5 } })),
    ];
    for (const lookup of lookups) {
        try {
            rememberPublicIds(await lookup());
        } catch {
            // Not an operator, or offline: the next lookup or "not found" decides.
        }
        if (idOfPublicId(ref)) throw redirect({ href: location.href, replace: true });
    }
    throw notFound();
}
